// src/hooks/useAsterisk.js
import { useState, useEffect, useCallback, useRef } from 'react';
import asteriskService from '../services/asteriskService';

const getVisibilityAwareDelay = (visibleDelay, hiddenDelay) => {
  if (typeof document === 'undefined') {
    return visibleDelay;
  }

  return document.hidden ? hiddenDelay : visibleDelay;
};

const toComparableTimestamp = (value) => {
  if (!value) return '';
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? '' : String(time);
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? String(value) : String(parsed);
};

const areActiveChannelsEqual = (prev, next) => {
  if (prev.length !== next.length) {
    return false;
  }

  return prev.every((item, idx) => {
    const candidate = next[idx];
    return item.channelId === candidate.channelId &&
      item.number === candidate.number &&
      String(item.calledNumber || '') === String(candidate.calledNumber || '') &&
      String(item.callerNumber || '') === String(candidate.callerNumber || '') &&
      item.status === candidate.status &&
      item.userId === candidate.userId &&
      String(item.queryId || '') === String(candidate.queryId || '') &&
      String(item.acceptedBy || '') === String(candidate.acceptedBy || '') &&
      String(item.acceptedAt || '') === String(candidate.acceptedAt || '') &&
      String(item.callId || '') === String(candidate.callId || '') &&
      String(item.uniqueId || '') === String(candidate.uniqueId || '') &&
      String(item.callStateUniqueId || '') === String(candidate.callStateUniqueId || '') &&
      String(item.ivrUid || '') === String(candidate.ivrUid || '') &&
      String(item.linkedId || '') === String(candidate.linkedId || '') &&
      String(item.stableId || '') === String(candidate.stableId || '') &&
      String(item.dialStatus || '') === String(candidate.dialStatus || '') &&
      String(item.sipProgressCode || '') === String(candidate.sipProgressCode || '') &&
      String(item.sipProgressText || '') === String(candidate.sipProgressText || '') &&
      String(item.sipProgress || '') === String(candidate.sipProgress || '') &&
      String(item.sipProgressAt || '') === String(candidate.sipProgressAt || '') &&
      Boolean(item.hasSipProgress) === Boolean(candidate.hasSipProgress) &&
      Boolean(item.hasSipRingbackEvent) === Boolean(candidate.hasSipRingbackEvent) &&
      String(item.sipResponseCode || '') === String(candidate.sipResponseCode || '') &&
      String(item.sipResponseText || '') === String(candidate.sipResponseText || '') &&
      String(item.sipResponse || '') === String(candidate.sipResponse || '') &&
      String(item.q850Cause || '') === String(candidate.q850Cause || '') &&
      String(item.q850Text || '') === String(candidate.q850Text || '') &&
      Number(item.duration || 0) === Number(candidate.duration || 0) &&
      toComparableTimestamp(item.startTime) === toComparableTimestamp(candidate.startTime) &&
      Boolean(item.isRecording) === Boolean(candidate.isRecording) &&
      String(item.recordingName || '') === String(candidate.recordingName || '') &&
      String(item.recordingStartedAt || '') === String(candidate.recordingStartedAt || '');
  });
};

const getCallResponseKey = (info = {}) => (
  [
    info.channelId || '',
    info.stableId || '',
    info.dialStatus || '',
    info.sipResponseCode || '',
    info.sipResponseText || '',
    info.q850Cause || '',
    info.q850Text || ''
  ].map((value) => String(value || '').trim()).join('|')
);

const hasFinalCallResponse = (info = {}) => {
  const sipCode = Number.parseInt(String(info.sipResponseCode || '').trim(), 10);
  const q850Cause = String(info.q850Cause || '').trim();
  const dialStatus = String(info.dialStatus || '').trim().toUpperCase();

  return (
    (Number.isFinite(sipCode) && sipCode >= 400) ||
    (q850Cause && q850Cause !== '16') ||
    ['BUSY', 'NOANSWER', 'CANCEL', 'CHANUNAVAIL', 'CONGESTION', 'FAILED'].includes(dialStatus)
  );
};

const areIncomingCallsEqual = (prev, next) => {
  if (prev.length !== next.length) {
    return false;
  }

  return prev.every((item, idx) => {
    const candidate = next[idx];
    return String(item.channelId || '') === String(candidate.channelId || '') &&
      String(item.callerNumber || '') === String(candidate.callerNumber || '') &&
      String(item.callerName || '') === String(candidate.callerName || '') &&
      String(item.queryId || '') === String(candidate.queryId || '') &&
      String(item.acceptedBy || '') === String(candidate.acceptedBy || '') &&
      String(item.acceptedAt || '') === String(candidate.acceptedAt || '') &&
      String(item.ivrUid || '') === String(candidate.ivrUid || '') &&
      String(item.linkedId || '') === String(candidate.linkedId || '') &&
      String(item.stableId || '') === String(candidate.stableId || '') &&
      Number(item.duration || 0) === Number(candidate.duration || 0) &&
      String(item.receivedAt || '') === String(candidate.receivedAt || '') &&
      String(item.assignedAgent || '') === String(candidate.assignedAgent || '') &&
      String(item.transferredByExtension || '') === String(candidate.transferredByExtension || '') &&
      String(item.transferredByName || '') === String(candidate.transferredByName || '') &&
      String(item.state || '') === String(candidate.state || '');
  });
};

export const useAsterisk = () => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [activeChannels, setActiveChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle, dialing, ringing, connected, ended
  const [lastCallResponse, setLastCallResponse] = useState(null);
  const failureCountRef = useRef(0);
  const currentChannelRef = useRef(null);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const incomingPollInFlightRef = useRef(false);

  useEffect(() => {
    currentChannelRef.current = currentChannel;
  }, [currentChannel]);

  // Update connection status
  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    const applyConnectionStatus = (nextStatus) => {
      setConnectionStatus((prev) => (prev === nextStatus ? prev : nextStatus));
    };

    const scheduleNext = (immediate = false) => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      const delay = immediate
        ? 0
        : getVisibilityAwareDelay(
          failureCountRef.current >= 3 ? 8000 : 3000,
          failureCountRef.current >= 3 ? 20000 : 10000
        );
      timeoutId = setTimeout(checkConnection, delay);
    };

    const checkConnection = async () => {
      try {
        const health = await asteriskService.getHealth();
        if (cancelled) return;
        applyConnectionStatus(health.ariConnected ? 'connected' : 'disconnected');
        failureCountRef.current = 0;
      } catch (error) {
        if (cancelled) return;
        applyConnectionStatus('disconnected');
        failureCountRef.current += 1;
        if (failureCountRef.current === 3) {
          console.warn('Slowing ARI health checks after repeated failures.');
        }
      } finally {
        scheduleNext();
      }
    };

    asteriskService.setStatusChangeCallback((channelId, status, channelInfo = null) => {
      if (channelInfo && hasFinalCallResponse(channelInfo)) {
        setLastCallResponse((prev) => {
          const next = { channelId, ...channelInfo };
          return getCallResponseKey(prev || {}) === getCallResponseKey(next) ? prev : next;
        });
      }

      if (channelId === currentChannelRef.current) {
        const dialStatus = String(channelInfo?.dialStatus || '').trim().toUpperCase();
        const sipCode = String(channelInfo?.sipResponseCode || '').trim();
        const answered =
          status === 'connected' ||
          dialStatus === 'ANSWER' ||
          sipCode === '200' ||
          Boolean(channelInfo?.acceptedAt && dialStatus === 'ANSWER');

        if (answered) {
          setCallStatus('in-call');
        } else if (status === 'connecting') {
          setCallStatus('connecting');
        } else if (status === 'ringing') {
          setCallStatus('ringing');
        } else if (status === 'failed') {
          setCallStatus('failed');
        } else if (status === 'feedback' || status === 'idle' || status === 'ended' || status === 'disconnected') {
          setCallStatus('idle');
          setCurrentChannel(null);
        }
      }
    });

    const handleVisibilityChange = () => {
      if (cancelled || typeof document === 'undefined' || document.hidden) {
        return;
      }

      failureCountRef.current = Math.min(failureCountRef.current, 2);
      scheduleNext(true);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    scheduleNext(true);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      asteriskService.setStatusChangeCallback(null);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []);

  // Update active channels (only when they actually change to avoid noisy rerenders)
  useEffect(() => {
    const updateChannels = () => {
      const next = asteriskService.getActiveChannels();

      setActiveChannels((prev) => (areActiveChannelsEqual(prev, next) ? prev : next));
    };

    const interval = setInterval(updateChannels, 1000);
    return () => clearInterval(interval);
  }, []);

  // Poll incoming calls so ARI-driven inbound calls surface in the UI
  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    const applyIncomingCalls = (nextCalls) => {
      setIncomingCalls((prev) => (areIncomingCallsEqual(prev, nextCalls) ? prev : nextCalls));
    };

    const scheduleNext = (immediate = false) => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      // Poll fast while the tab is visible so inbound queue calls surface in the
      // popup quickly — a slow poll made the popup appear after the call had
      // already dropped ("cut before popup"). Slower when the tab is hidden.
      timeoutId = setTimeout(
        pollIncoming,
        immediate ? 0 : getVisibilityAwareDelay(1000, 4000)
      );
    };

    const pollIncoming = async () => {
      if (incomingPollInFlightRef.current) {
        scheduleNext();
        return;
      }

      incomingPollInFlightRef.current = true;
      try {
        const result = await asteriskService.getIncomingCalls();
        if (cancelled) return;
        if (result.success) {
          applyIncomingCalls(result.calls || []);
        } else if (result.offline) {
          setConnectionStatus((prev) => (prev === 'disconnected' ? prev : 'disconnected'));
          applyIncomingCalls([]);
        } else {
          applyIncomingCalls([]);
        }
      } catch (error) {
        if (!cancelled) {
          applyIncomingCalls([]);
        }
      } finally {
        incomingPollInFlightRef.current = false;
        scheduleNext();
      }
    };

    const handleVisibilityChange = () => {
      if (cancelled || typeof document === 'undefined' || document.hidden) {
        return;
      }

      scheduleNext(true);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    scheduleNext(true);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []);

  // Make a call
  const makeCall = useCallback(async (number, callerId = null, userId = null, agentId = null) => {
    // Check if already have active call
    if (currentChannel && callStatus !== 'idle') {
      throw new Error('You already have an active call. Please end it before making a new call.');
    }

    setLastCallResponse(null);
    setCallStatus('dialing');

    try {
      const result = await asteriskService.makeCall(number, callerId, userId, agentId);

      if (result.success) {
        setLastCallResponse(null);
        setCurrentChannel(result.channelId);
        setCallStatus('dialing');
        return result;
      }

      setCallStatus('idle');
      if (result.code === 'CALL_ALREADY_ACTIVE') {
        throw new Error('You can only have one active call at a time');
      }
      throw new Error(result.error);
    } catch (error) {
      setCallStatus('idle');
      throw error;
    }
  }, [currentChannel, callStatus]);

  // Send DTMF
  const sendDTMF = useCallback(async (digits) => {
    if (!currentChannel) {
      throw new Error('No active channel for DTMF');
    }

    return await asteriskService.sendDTMF(currentChannel, digits);
  }, [currentChannel]);

  const acceptIncomingCall = useCallback(async (channelId, agentId) => {
    setCallStatus('connecting');

    try {
      const result = await asteriskService.acceptIncomingCall(channelId, agentId);
      if (!result.success) {
        setCallStatus('idle');
        return result;
      }

      const trackedChannelId = result.callInfo?.channelId || channelId;
      setCurrentChannel(trackedChannelId);
      setCallStatus('connecting');
      setIncomingCalls((prev) => prev.filter((call) => call.channelId !== trackedChannelId));

      // Keep status synced
      asteriskService.startStatusPolling(trackedChannelId);

      return result;
    } catch (error) {
      setCallStatus('idle');
      return { success: false, error: error.message };
    }
  }, []);

  const rejectIncomingCall = useCallback(async (channelId, agentId = '') => {
    const normalizedChannelId = String(channelId || '').trim();
    if (!normalizedChannelId) {
      return { success: false, error: 'channelId is required' };
    }

    try {
      const result = await asteriskService.hangupCall(normalizedChannelId, {
        action: 'reject',
        agentId
      });
      if (!result?.success) {
        return result || { success: false, error: 'Reject call failed' };
      }

      setIncomingCalls((prev) =>
        prev.filter((call) => String(call.channelId || '').trim() !== normalizedChannelId)
      );
      if (String(currentChannelRef.current || '').trim() === normalizedChannelId) {
        setCurrentChannel(null);
      }
      setCallStatus('idle');

      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Reject call failed' };
    }
  }, []);

  const resetCurrentCall = useCallback((channelId = '') => {
    asteriskService.resetCurrentCall(channelId || currentChannel || '');
    setCurrentChannel(null);
    setCallStatus('idle');
    setIncomingCalls([]);
  }, [currentChannel]);

  // Hangup call
  const hangupCall = useCallback(async (channelId = null) => {
    const targetChannel = channelId || currentChannel;

    if (!targetChannel) {
      throw new Error('No channel to hangup');
    }

    setCallStatus('ending');

    try {
      const result = await asteriskService.hangupCall(targetChannel);

      if (result.success && targetChannel === currentChannel) {
        setCurrentChannel(null);
        setCallStatus('idle');
      }

      return result;
    } catch (error) {
      setCallStatus('idle');
      throw error;
    }
  }, [currentChannel]);

  // Emergency hangup all calls
  const hangupAllCalls = useCallback(async () => {
    setCallStatus('ending');

    try {
      const result = await asteriskService.hangupAllCalls();

      if (result.success) {
        setCurrentChannel(null);
        setCallStatus('idle');
      }

      return result;
    } catch (error) {
      setCallStatus('idle');
      throw error;
    }
  }, []);

  // Get connection info
  const getConnectionInfo = useCallback(async () => {
    try {
      return await asteriskService.getHealth();
    } catch (error) {
      return null;
    }
  }, []);

  return {
    // Connection status
    connectionStatus,
    isConnected: connectionStatus === 'connected',

    // Call management
    makeCall,
    sendDTMF,
    hangupCall,
    hangupAllCalls,
    acceptIncomingCall,
    rejectIncomingCall,
    resetCurrentCall,

    // State
    activeChannels,
    currentChannel,
    callStatus,
    lastCallResponse,
    incomingCalls,

    // Utilities
    getConnectionInfo
  };
};
